// Pure CTA selection — given a lightweight context (route + report
// signals) return the single most relevant Sked CTA, or null if no
// rule applies. The evaluator is deliberately dependency-free so it
// unit tests trivially and can be re-used on the server if we ever
// want to render CTAs into digest emails.

export type CtaSlug =
  | "fix_utms"
  | "schedule_with_sked"
  | "start_scheduling"
  | "share_brand_reports"
  | "skip_weekly_pull";

export interface CtaCopy {
  slug: CtaSlug;
  /** 2–4 word primary label. */
  headline: string;
  /** One-sentence supporting copy (≤ 90 chars). */
  body: string;
  /** CTA button text. */
  action: string;
  /** The lead-event the onClick should fire. */
  event: "trial_cta_clicked";
}

export const CTA_COPY: Record<CtaSlug, CtaCopy> = {
  fix_utms: {
    slug: "fix_utms",
    headline: "Fix UTMs with Sked",
    body: "Auto-tag every link from your Sked posts — no more broken attribution.",
    action: "Fix my UTMs",
    event: "trial_cta_clicked",
  },
  schedule_with_sked: {
    slug: "schedule_with_sked",
    headline: "Schedule with Sked",
    body: "Keep the momentum — bulk schedule across Instagram, TikTok, LinkedIn in one calendar.",
    action: "Start scheduling",
    event: "trial_cta_clicked",
  },
  start_scheduling: {
    slug: "start_scheduling",
    headline: "Start scheduling social",
    body: "Social traffic starts with social posts. Try Sked free for 7 days.",
    action: "Try Sked free",
    event: "trial_cta_clicked",
  },
  share_brand_reports: {
    slug: "share_brand_reports",
    headline: "Share brand-consistent reports",
    body: "Send white-labelled exports from Sked, scheduled for any team or client.",
    action: "See Sked reporting",
    event: "trial_cta_clicked",
  },
  skip_weekly_pull: {
    slug: "skip_weekly_pull",
    headline: "Skip the weekly pull",
    body: "Sked auto-generates your weekly social report and emails it to your team.",
    action: "Automate my reports",
    event: "trial_cta_clicked",
  },
};

export type CtaRoute =
  | "/app/report"
  | "/app/exports"
  | "/app/settings"
  | "/app/utm-health"
  | "/app/connect"
  | "other";

export interface CtaPayloadSignals {
  /** 0–100 UTM hygiene score. */
  utmScore?: number;
  /** Social-acquisition sessions in the last range. */
  socialSessions?: number;
  /** True when the user landed on an empty-state surface. */
  isEmptyState?: boolean;
  /** True once the user successfully generated a PDF/XLSX export. */
  hasExport?: boolean;
  /** True when the user is currently configuring / confirmed a weekly digest. */
  digestJustConfirmed?: boolean;
}

export interface CtaContext {
  route: CtaRoute;
  signals: CtaPayloadSignals;
}

/** Ordered rule evaluation — first match wins. Undefined signals are
 *  treated as "don't know, don't fire". */
export function selectCta(ctx: CtaContext): CtaCopy | null {
  const { route, signals } = ctx;

  // 1. Empty-state trumps everything — no data means no report-shape rule
  //    applies, the user needs a reason to come back.
  if (signals.isEmptyState) return CTA_COPY.start_scheduling;

  // 2. Digest-confirmed is a moment-of-intent — catch it before any
  //    report-shape rules, since it can overlap both strong/poor signals.
  if (signals.digestJustConfirmed) return CTA_COPY.skip_weekly_pull;

  // 3. Export generated — offer the Sked reporting upsell.
  if (signals.hasExport && route === "/app/exports") return CTA_COPY.share_brand_reports;

  // 4. Poor UTM score — most impactful fix-path for tool_source users.
  if (typeof signals.utmScore === "number" && signals.utmScore < 50) {
    return CTA_COPY.fix_utms;
  }

  // 5. Default to scheduling — fires on any report surface that didn't
  //    trigger a more specific rule above. Previously gated on
  //    socialSessions >= 1000, removed so every viewer sees the CTA.
  return CTA_COPY.schedule_with_sked;
}

// Single source of truth for outbound links from the Traffic Snapshot
// tool back to skedsocial.com. Every surface (sidebar, footer, share
// viewer, emails, PDF, landing page) must route through buildSkedHref
// so utm_source stays consistent and the marketing side can slice
// attribution cleanly by medium / campaign / content.
export const SKED_ORIGIN = "https://skedsocial.com";
export const SKED_UTM_SOURCE = "traffic-snapshot";

export interface SkedHrefOptions {
  /** utm_medium — what kind of touchpoint (e.g. "app_sidebar", "email"). Defaults to "tool". */
  medium?: string;
  /** utm_campaign — what the call-to-action is. Required. */
  campaign: string;
  /** utm_content — finer-grained slot identifier within the surface. Optional. */
  content?: string;
  /** Path on skedsocial.com (e.g. "/pricing"). Defaults to "/". */
  path?: string;
}

export function buildSkedHref(opts: SkedHrefOptions): string {
  const params = new URLSearchParams({
    utm_source: SKED_UTM_SOURCE,
    utm_medium: opts.medium ?? "tool",
    utm_campaign: opts.campaign,
  });
  if (opts.content) params.set("utm_content", opts.content);
  const path = opts.path ?? "/";
  return `${SKED_ORIGIN}${path}?${params.toString()}`;
}

/** UTM-tagged link for the in-app contextual CTA panel. Thin wrapper
 *  over buildSkedHref so panel callers don't have to specify medium. */
export function buildCtaHref(slug: CtaSlug): string {
  return buildSkedHref({ medium: "tool", campaign: slug });
}
